/**
 * React Query hook for managing opportunity alert subscriptions via morgans Connect RPC.
 */ import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { morgansClient } from '../../../lib/connect/clients';
/**
 * `seed` primes the status from data the caller already has — the opportunities
 * list resolves each row's per-user alert status server-side (Opportunity.isAlerted,
 * batch-looked-up), so a card that passes it in avoids a redundant per-card GET.
 * Without a seed the hook fetches.
 */ export function useOpportunityAlert(noticeId, seed) {
    const queryClient = useQueryClient();
    const queryKey = [
        'opportunity',
        'alert',
        noticeId
    ];
    // Fetch current alert status — unless seeded from the list, in which case we
    // hold that value and skip the GET (see useSavedOpportunity for the rationale).
    const { data: alertStatus, isLoading: isLoadingStatus } = useQuery({
        queryKey,
        queryFn: async ()=>{
            const response = await morgansClient.getAlertStatus({
                noticeId
            });
            return {
                enabled: response.enabled
            };
        },
        staleTime: 5 * 60 * 1000,
        ...seed ? {
            initialData: {
                enabled: seed.enabled
            },
            enabled: false
        } : {}
    });
    // Toggle alert mutation. A seeded query is `enabled: false`, and
    // invalidateQueries does not refetch a disabled query — so onMutate's
    // optimistic write is what makes the bell flip immediately, and onSuccess's
    // setQueryData (rather than a refetch) is what reconciles it against the
    // server's actual value once the mutation lands. Mirrors useSavedOpportunity.
    const toggleMutation = useMutation({
        mutationFn: async (enabled)=>{
            const response = await morgansClient.setAlert({
                noticeId,
                enabled
            });
            return {
                alert: {
                    opportunityId: noticeId,
                    enabled: response.enabled
                }
            };
        },
        onMutate: async (enabled)=>{
            await queryClient.cancelQueries({
                queryKey
            });
            const previousStatus = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, {
                enabled
            });
            return {
                previousStatus
            };
        },
        onError: (_err, _enabled, context)=>{
            if (context?.previousStatus) {
                queryClient.setQueryData(queryKey, context.previousStatus);
            }
            // A seed observed mid-mutation was recorded as "seen" (see the sync
            // effect below) without being written to the cache, deferring to the
            // mutation while it was in flight. Now that it's failed and the cache
            // has been rolled back to the pre-mutation value, that deferred seed
            // is no longer superseded by anything more authoritative — clear the
            // marker so the sync effect treats the current seed as unapplied and
            // writes it once `isPending` clears, instead of skipping it forever.
            lastSyncedSeedRef.current = undefined;
        },
        onSuccess: (data)=>{
            queryClient.setQueryData(queryKey, {
                enabled: data.alert.enabled
            });
        },
        onSettled: ()=>{
            queryClient.invalidateQueries({
                queryKey
            });
        }
    });
    // initialData only seeds the cache entry when it's first CREATED — if this
    // card re-renders with a CHANGED seed (the list refetched and now reports a
    // different isAlerted), the existing entry is reused and the disabled query
    // has no way to pick it up on its own. Sync it in directly, but never while
    // a toggle is in flight — that would clobber the user's just-clicked
    // optimistic value with a now-stale list value.
    //
    // Compares against the LAST SEED WE'VE APPLIED (a ref), not the live cache
    // value: the cache legitimately diverges from seed the instant a toggle's
    // onMutate/onSuccess writes to it, and seed itself doesn't catch up until
    // the parent list refetches. Comparing to the cache would make this effect
    // fire the moment `isPending` flips back to `false` and stomp that fresh
    // write with the still-stale seed prop.
    const lastSyncedSeedRef = useRef(undefined);
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally scoped to the seed value + mutation-pending flag — queryClient/queryKey are stable per noticeId, re-running on their identity would defeat the point of this sync.
    useEffect(()=>{
        if (!seed) return;
        const last = lastSyncedSeedRef.current;
        const changed = !last || last.enabled !== seed.enabled;
        if (!changed) return;
        if (toggleMutation.isPending) {
            // A seed that changes mid-mutation is necessarily a pre-mutation read —
            // the list can't yet know the mutation's outcome. Record it as "seen"
            // so we don't re-diff against a stale `last` once the mutation settles
            // (that would read as "seed changed again" and clobber the mutation's
            // committed value), but do NOT write it to the cache: while a toggle is
            // in flight, the optimistic/authoritative value belongs to the mutation.
            lastSyncedSeedRef.current = seed;
            return;
        }
        lastSyncedSeedRef.current = seed;
        queryClient.setQueryData(queryKey, {
            enabled: seed.enabled
        });
    }, [
        seed?.enabled,
        toggleMutation.isPending
    ]);
    return {
        isEnabled: alertStatus?.enabled ?? false,
        isLoading: isLoadingStatus || toggleMutation.isPending,
        toggleAlert: (enabled)=>toggleMutation.mutate(enabled),
        error: toggleMutation.error
    };
}
